import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Grid, Paper, Stack, Typography, Avatar, IconButton } from '@mui/material';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from './WebSocketContext';
import { useTranslation } from 'react-i18next';
import logo from '../images/logowithname2.png';

const primaryColor = '#5757b0';
const primaryHoverColor = '#4545a0';

const ReadOnlyProjectDashboard = () => {
    const { sendMessage, subscribeToMessages, isConnected } = useWebSocket();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [serverVersion, setServerVersion] = useState(null);

    // -- helpers copied from sidebar for consistent avatar styling --
    const avatarColors = ['#b39ddb', '#90caf9', '#a5d6a7', '#ef9a9a', '#ffcc80', '#80cbc4', '#b39ddb', '#ffab91', '#81d4fa', '#f48fb1'];
    const getColorFromString = (input) => {
        const s = String(input || '');
        if (!s) return avatarColors[0];
        let hash = 0;
        for (let i = 0; i < s.length; i++) {
            hash = s.charCodeAt(i) + ((hash << 5) - hash);
            hash = hash & hash;
        }
        return avatarColors[Math.abs(hash) % avatarColors.length];
    };

    const getInitials = (text) => {
        const s = String(text || '').trim();
        if (!s) return '?';
        const words = s.split(/\s+/).filter(Boolean);
        if (words.length > 1) {
            return (words[0][0] + (words[1][0] || '')).slice(0, 2).toUpperCase();
        }
        return s.slice(0, 2).toUpperCase();
    };

    // animated avatar styling matches sidebar exactly
    const getAnimatedAvatarSx = (title) => ({
        width: { xs: 28, sm: 30, md: 32 },
        height: { xs: 28, sm: 30, md: 32 },
        fontSize: { xs: '0.75rem', sm: '0.85rem' },
        bgcolor: getColorFromString(title),
        color: '#fff',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        flexShrink: 0,
        transition: 'transform 0.35s ease, box-shadow 0.35s ease',
        boxShadow: '0 4px 10px rgba(15, 23, 42, 0.12)',
        '.MuiPaper-root:hover &': {
            transform: 'translateX(2px) scale(1.05)',
            boxShadow: '0 8px 18px rgba(15, 23, 42, 0.18)'
        }
    });

    const displayedProjects = useMemo(() => {
        const parseTimestamp = (value) => {
            if (!value) {
                return 0;
            }
            const parsedDate = new Date(value);
            const timestamp = parsedDate.getTime();
            return Number.isFinite(timestamp) ? timestamp : 0;
        };

        return [...projects]
            .sort((firstProject, secondProject) => {
                const firstScore = parseTimestamp(firstProject.last_visit) || parseTimestamp(firstProject.createTime);
                const secondScore = parseTimestamp(secondProject.last_visit) || parseTimestamp(secondProject.createTime);
                return secondScore - firstScore;
            })
            .slice(0, 4);
    }, [projects]);

    const formatDateOrFallback = useCallback((value, fallbackText) => {
        if (!value) {
            return fallbackText;
        }

        const parsedDate = new Date(value);
        if (Number.isNaN(parsedDate.getTime())) {
            return fallbackText;
        }

        return new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(parsedDate);
    }, []);

    const requestProjects = useCallback(() => {
        if (!sendMessage || !isConnected) {
            return;
        }
        setLoading(true);
        setError('');
        sendMessage({ command: 'getcases' });
    }, [isConnected, sendMessage]);

    useEffect(() => {
        const unsubscribe = subscribeToMessages(['listcases'], (message) => {
            const payload = message?.data || {};
            if (payload.error) {
                setError(String(payload.error));
                setProjects([]);
                setLoading(false);
                return;
            }
            const caseItems = Array.isArray(payload.cases) ? payload.cases : [];
            setProjects(caseItems);
            setLoading(false);
        });
        return unsubscribe;
    }, [subscribeToMessages]);

    useEffect(() => {
        const unsubscribe = subscribeToMessages(['certification', 'configuration_data'], (message) => {
            const version = message?.data?.server_version;
            if (version) {
                setServerVersion(version);
            }
        });
        return unsubscribe;
    }, [subscribeToMessages]);

    useEffect(() => {
        if (isConnected) {
            requestProjects();
        }
    }, [isConnected, requestProjects]);

    useEffect(() => {
        let mounted = true;
        fetch('/api/server_status')
            .then((response) => (response.ok ? response.json() : Promise.reject()))
            .then((data) => {
                if (mounted && data && data.server_version) {
                    setServerVersion(data.server_version);
                }
            })
            .catch(() => { });

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!isConnected || !sendMessage) {
            return;
        }
        sendMessage({ command: 'configuration_check' });
    }, [isConnected, sendMessage]);

    const openProject = useCallback((projectId) => {
        if (!projectId) {
            return;
        }
        navigate(`/case/${projectId}`);
    }, [navigate]);

    const openSidebar = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.dispatchEvent(new CustomEvent('dashboard:open-sidebar'));
    }, []);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: { xs: 2, sm: 3 },
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: '#f7f7ff',
                backgroundImage: 'linear-gradient(135deg, #f7f7ff 0%, #ffffff 72%, rgba(87, 87, 176, 0.07) 100%)',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: { xs: -10, sm: -18 },
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 'min(440px, 78vw)',
                    display: 'flex',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: '-20% -8%',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(87, 87, 176, 0.15) 0%, rgba(87, 87, 176, 0) 70%)',
                        filter: 'blur(18px)',
                    }}
                />
                <Box
                    component="img"
                    src={logo}
                    alt=""
                    sx={{
                        width: '100%',
                        maxWidth: 420,
                        opacity: 0.16,
                        filter: 'grayscale(1) contrast(1.03)',
                        userSelect: 'none',
                    }}
                />
            </Box>

            <Box sx={{ width: '100%', maxWidth: 980, position: 'relative', zIndex: 1 }}>
                {loading && (
                    <Stack
                        spacing={2}
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            minHeight: '50vh',
                            width: '100%',
                        }}
                    >
                        <CircularProgress
                            size={56}
                            thickness={4}
                            sx={{ color: primaryColor }}
                        />
                        <Box
                            sx={{
                                width: 96,
                                height: 6,
                                borderRadius: 999,
                                backgroundColor: 'rgba(87, 87, 176, 0.12)',
                                overflow: 'hidden',
                                position: 'relative',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    height: '100%',
                                    width: '38%',
                                    borderRadius: 999,
                                    background: 'linear-gradient(90deg, rgba(87, 87, 176, 0.2) 0%, rgba(87, 87, 176, 0.95) 100%)',
                                    animation: 'dashboardLoaderSlide 1.1s ease-in-out infinite',
                                }}
                            />
                        </Box>
                        <style>
                            {`
                                @keyframes dashboardLoaderSlide {
                                    0% { transform: translateX(-120%); }
                                    100% { transform: translateX(300%); }
                                }
                            `}
                        </style>
                    </Stack>
                )}

                {!loading && error && (
                    <Stack spacing={1.5} sx={{ py: 1 }}>
                        <Typography variant="body2" color="error">{error}</Typography>
                        <Button variant="outlined" onClick={requestProjects} sx={{ width: 'fit-content' }}>
                            {t('homeDenied.retry')}
                        </Button>
                    </Stack>
                )}

                {!loading && !error && projects.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {t('homeDenied.noProjects')}
                    </Typography>
                )}

                {!loading && !error && displayedProjects.length > 0 && (
                    <Stack spacing={2.5} alignItems="center">
                        <Grid container spacing={2.5} justifyContent="center">
                            {displayedProjects.map((project) => (
                                <Grid key={project.id} item xs={12} sm={6} md={5}>
                                    <Paper
                                        component="button"
                                        type="button"
                                        onClick={() => openProject(project.id)}
                                        elevation={0}
                                        sx={{
                                            width: '100%',
                                            textAlign: 'left',
                                            p: 2,
                                            borderRadius: 2.5,
                                            border: '1px solid rgba(87, 87, 176, 0.22)',
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 6px 18px rgba(69, 69, 160, 0.08)',
                                            cursor: 'pointer',
                                            transition: 'transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease, background-color 0.16s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                borderColor: primaryColor,
                                                backgroundColor: '#fcfcff',
                                                boxShadow: '0 10px 24px rgba(69, 69, 160, 0.14)',
                                            },
                                            '&:active': {
                                                transform: 'translateY(0)',
                                            },
                                            '&:focus-visible': {
                                                outline: 'none',
                                                boxShadow: '0 0 0 3px rgba(87, 87, 176, 0.24)',
                                            },
                                        }}
                                    >
                                        <Stack spacing={1.2}>
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                                    {/* coloured identifier avatar, matching sidebar style */}
                                                    <Avatar
                                                        sx={getAnimatedAvatarSx(project.title)}
                                                    >
                                                        {getInitials(project.title)}
                                                    </Avatar>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: '#2f2f6d',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 1,
                                                            WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {project.title || t('sidebar.untitled_case')}
                                                    </Typography>
                                                </Box>
                                            </Stack>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                    minHeight: 38,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                }}
                                            >
                                                {project.description || t('sidebar.no_description')}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    pt: 0.6,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.6,
                                                    width: 'fit-content',
                                                    px: 3,
                                                    py: 1.2,
                                                    borderRadius: 99,
                                                    backgroundColor: 'rgba(87, 87, 176, 0.1)',
                                                    color: primaryColor,
                                                    border: '1px solid rgba(87, 87, 176, 0.24)',
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                    {t('homeDenied.openProject')}
                                                </Typography>
                                                <ArrowOutwardRoundedIcon sx={{ fontSize: 14, color: primaryColor }} />
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>

                        {projects.length > displayedProjects.length && (
                            <IconButton
                                onClick={openSidebar}
                                aria-label={t('sidebar.workspace', 'Workspace')}
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: '18px',
                                    border: '1px solid rgba(87, 87, 176, 0.22)',
                                    backgroundColor: '#ffffff',
                                    color: primaryColor,
                                    boxShadow: '0 10px 24px rgba(69, 69, 160, 0.12)',
                                    transition: 'transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease',
                                    '&:hover': {
                                        backgroundColor: '#fcfcff',
                                        borderColor: primaryColor,
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 14px 28px rgba(69, 69, 160, 0.16)',
                                    },
                                }}
                            >
                                <MoreHorizRoundedIcon />
                            </IconButton>
                        )}
                    </Stack>
                )}
            </Box>

            <Box
                sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    zIndex: 1,
                }}
            >
                <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                    {`Version: ${serverVersion ? `Server: ${serverVersion}` : 'Server: unknown'}`}
                </Typography>
            </Box>
        </Box>
    );
};

export default ReadOnlyProjectDashboard;
